import React, { useState, useRef } from 'react';
import { Bot, ListTree, Map, Clock } from 'lucide-react';
import { AIChat } from './ai/AIChat';

interface TaskAIPanelProps {
  taskId: string;
  taskTitle: string;
  position?: 'fixed' | 'relative';
  className?: string;
}

export const TaskAIPanel: React.FC<TaskAIPanelProps> = ({
  taskId,
  taskTitle,
  position = 'fixed',
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const sendMessageRef = useRef<((message: string) => void) | null>(null);

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className={`bg-blue-600 text-white p-3 rounded-full shadow-lg hover:bg-blue-700 transition-colors z-50 ${
          position === 'fixed'
            ? 'fixed bottom-4 right-4'
            : 'relative'
        } ${className}`}
        title={`Get AI help with "${taskTitle}"`}
      >
        <Bot className="h-6 w-6" />
      </button>
    );
  }

  const quickActions = [
    { label: 'Break Down', icon: ListTree, prompt: 'Break this task into subtasks' },
    { label: 'Plan', icon: Map, prompt: 'Help me plan how to approach this task' },
    { label: 'Estimate', icon: Clock, prompt: 'Estimate how long this task will take' },
  ];

  return (
    <div className={`w-96 z-50 shadow-xl flex flex-col ${
      position === 'fixed'
        ? 'fixed bottom-4 right-4 h-[32rem]'
        : 'absolute top-0 right-0 h-96'
    } ${className}`}>
      {/* Quick actions */}
      <div className="flex gap-1.5 p-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-t-lg border-b-0">
        {quickActions.map(({ label, icon: Icon, prompt }) => (
          <button
            key={label}
            onClick={() => sendMessageRef.current?.(prompt)}
            className="flex items-center gap-1 px-2 py-1 text-xs rounded-md bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-blue-50 dark:hover:bg-blue-900/30 hover:border-blue-300 dark:hover:border-blue-600 transition-colors"
          >
            <Icon className="w-3 h-3" />
            {label}
          </button>
        ))}
      </div>
      <AIChat
        onClose={() => setIsOpen(false)}
        className="flex-1 min-h-0 rounded-t-none"
        taskId={taskId}
        taskTitle={taskTitle}
        onSendMessageRef={sendMessageRef}
      />
    </div>
  );
};
